<template>
  <div
    ref="container"
    class="multi-stacked-bar-chart-container"
    style="height: 100%;"
  >
    <svg ref="chartSvg"><!--SVG--></svg>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
    name: 'MultiSeriesStackedBarChart',
    props: {
        data: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            svgWidth: 0,
            svgHeight: 0,
        };
    },
    computed: {
        totalSvgHeight() {
            return this.svgHeight;
        },
        processedData() {
            return this.data.map(series => {
                if (!series.data || series.data.length === 0) {
                    return { name: series.name, values: [] };
                }
                const { norm } = series.data[series.data.length - 1].metadata || {};
                if (!norm || !(norm.minimum || norm.low || norm.high || norm.maximum)) {
                    return { name: series.name, values: [] };
                }
                const counts = {
                    outsideLow: 0,
                    lowVeryLow: 0,
                    lowMin: 0,
                    minMax: 0,
                    maxHigh: 0,
                    highVeryHigh: 0,
                    aboveMax: 0,
                };
                series.data.forEach(({ value }) => {
                    if (value < norm.veryLow) counts.outsideLow += 1;
                    else if (value < norm.low) counts.lowVeryLow += 1;
                    else if (value < norm.minimum) {
                        if (norm.low === null) {
                            counts.outsideLow += 1;
                        } else {
                            counts.lowMin += 1;
                        }
                    } else if (value < norm.maximum || norm.maximum === null) counts.minMax += 1;
                    else if (value < norm.high) counts.maxHigh += 1;
                    else if (value < norm.veryHigh) counts.highVeryHigh += 1;
                    else counts.aboveMax += 1;
                });
                return {
                    name: series.name,
                    values: Object.entries(counts).map(([key, count]) => ({
                        label: this.$t(`graphics.${key}`),
                        value: count,
                    })),
                };
            });
        },
        globalMaxTotal() {
            const totals = this.processedData.map(seriesObj => seriesObj.values.reduce((sum, d) => sum + d.value, 0));
            return d3.max(totals) || 1;
        },
    },
    watch: {
        data() {
            this.renderChart();
        },
    },
    mounted() {
        this.updateDimensions();
        this.renderChart();
        window.addEventListener('resize', this.onResize);
    },
    updated() {
        this.renderChart();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    },
    methods: {
        updateDimensions() {
            if (this.$refs.container) {
                this.svgWidth = this.$refs.container.clientWidth;
                this.svgHeight = this.$refs.container.clientHeight;
            } else {
                this.svgWidth = 600;
                this.svgHeight = 400;
            }
        },
        onResize() {
            this.updateDimensions();
            this.renderChart();
        },
        renderChart() {
            const svg = d3.select(this.$refs.chartSvg);
            const topMargin = 30;
            const bottomMargin = 60;
            const margin = { top: 10, right: 20, bottom: 0, left: 40 };
            const totalHeight = this.totalSvgHeight;
            const innerWidth = this.svgWidth - margin.left - margin.right;
            const innerHeight = totalHeight - topMargin - bottomMargin;

            svg
                .attr('viewBox', `0 0 ${this.svgWidth} ${totalHeight}`)
                .style('width', '100%')
                .style('height', '100%');
            svg.selectAll('*').remove();

            svg.append('text')
                .attr('x', this.svgWidth / 2)
                .attr('y', topMargin * 0.8)
                .attr('text-anchor', 'middle')
                .style('font-size', '20px')
                .style('font-weight', 'bold')
                .style('fill', '#333')
                .text(this.$t('graphics.barChartTitle'));

            const chartGroup = svg.append('g')
                .attr('transform', `translate(${margin.left}, ${topMargin})`);

            const xScale = d3.scaleBand()
                .domain(this.processedData.map(d => d.name))
                .range([0, innerWidth])
                .padding(0.3);

            const yScale = d3.scaleLinear()
                .domain([0, this.globalMaxTotal])
                .range([innerHeight, 0]);

            chartGroup.append('g')
                .attr('class', 'y-axis')
                .call(d3.axisLeft(yScale).ticks(5))
                .selectAll('text')
                .style('font-size', '10px');

            const xAxisGroup = chartGroup.append('g')
                .attr('class', 'x-axis')
                .attr('transform', `translate(0, ${innerHeight})`)
                .call(d3.axisBottom(xScale));

            xAxisGroup.selectAll('text')
                .call(this.wrapText, xScale.bandwidth())
                .attr('transform', 'translate(0, 10)')
                .style('font-size', '12px')
                .style('fill', '#333');

            const categories = [
                'outsideLow',
                'lowVeryLow',
                'lowMin',
                'minMax',
                'maxHigh',
                'highVeryHigh',
                'aboveMax',
            ];
            const color = d3.scaleOrdinal()
                .domain(categories)
                .range(['#e51f1f', '#f2a134', '#f7e379', '#44ce1b', '#f7e379', '#f2a134', '#e51f1f']);

            let tooltip = d3.select('body').select('.stacked-tooltip');
            if (tooltip.empty()) {
                tooltip = d3.select('body')
                    .append('div')
                    .attr('class', 'stacked-tooltip')
                    .style('position', 'absolute')
                    .style('padding', '5px 10px')
                    .style('background', 'rgba(0, 0, 0, 0.7)')
                    .style('color', '#fff')
                    .style('border-radius', '3px')
                    .style('pointer-events', 'none')
                    .style('font-size', '12px')
                    .style('display', 'none');
            }

            const barWidth = xScale.bandwidth() * 0.2;

            this.processedData.forEach(seriesObj => {
                const xPos = xScale(seriesObj.name) + (xScale.bandwidth() - barWidth) / 2;
                if (seriesObj.values.length === 0) {
                    chartGroup.append('text')
                        .attr('x', xScale(seriesObj.name) + xScale.bandwidth() / 2)
                        .attr('y', innerHeight - 5)
                        .attr('text-anchor', 'middle')
                        .style('fill', 'red')
                        .style('font-size', '10px')
                        .style('font-weight', 'bold')
                        .text(this.$t('graphics.noNorm'));
                    return;
                }
                let cumulative = 0;
                seriesObj.values.forEach(d => {
                    const y0 = cumulative;
                    cumulative += d.value;
                    const rectHeight = yScale(y0) - yScale(cumulative);
                    const rectY = yScale(cumulative);
                    chartGroup.append('rect')
                        .attr('x', xPos)
                        .attr('y', rectY)
                        .attr('width', barWidth)
                        .attr('height', 0)
                        .attr('fill', color(d.label))
                        .attr('stroke', 'white')
                        .style('stroke-width', '1px')
                        .on('mouseover', () => {
                            tooltip.style('display', 'block')
                                .html(`${d.label}<br>${this.$t('graphics.count')}: ${d.value}`);
                        })
                        .on('mousemove', event => {
                            tooltip.style('left', `${event.pageX + 10}px`)
                                .style('top', `${event.pageY + 10}px`);
                        })
                        .on('mouseout', () => {
                            tooltip.style('display', 'none');
                        })
                        .transition()
                        .duration(1000)
                        .attr('height', rectHeight);
                });
            });
        },
        wrapText(textSelection, maxWidth) {
            textSelection.each(function wrapTextFunction() {
                const textEl = d3.select(this);
                const words = textEl.text().split(/\s+/).reverse();
                let word;
                let line = [];
                let lineNumber = 0;
                const lineHeight = 1;
                const x = textEl.attr('x') || 0;
                const y = textEl.attr('y') || 0;
                textEl.text(null);
                let tspan = textEl
                    .append('tspan')
                    .attr('x', x)
                    .attr('y', y)
                    .attr('dy', '0em');
                while (words.length > 0) {
                    word = words.pop();
                    line.push(word);
                    tspan.text(line.join(' '));
                    if (tspan.node().getComputedTextLength() > maxWidth) {
                        line.pop();
                        tspan.text(line.join(' '));
                        line = [word];
                        lineNumber += 1;
                        tspan = textEl
                            .append('tspan')
                            .attr('x', x)
                            .attr('y', y)
                            .attr('dy', `${lineNumber * lineHeight }em`)
                            .text(word);
                    }
                }
                textEl.append('tspan')
                    .attr('x', x)
                    .attr('y', y)
                    .attr('dy', '0.2em')
                    .text('');
            });
        },
    },
};
</script>

  <style scoped>
  .multi-stacked-bar-chart-container {
    width: 100%;
  }
  .stacked-tooltip {
    z-index: 1000;
  }
  </style>
